import React from "react";
import { withStyles } from "@material-ui/core";
import { connect } from "react-redux";
import { I18n } from "@aws-amplify/core";
import { get, map, filter, size } from "lodash";
import Grid from "@material-ui/core/Grid";
import Box from "@material-ui/core/Box";

import DatePicker from "../../Common/DatePicker";
import FormSection from "../../Common/FormSection";
import ProfileSearchInput from "../../Common/ProfileSearchInput";
import AddOccurrenceDialog from "../AddOccurrence/AddOccurrenceDialog";
import { calendarViews, HSP } from "../../../constants/common";
import { formatDate, setToMidnight } from "../../../utils/date";
import images from "../../../assets/img/images";
import colors from "../../../assets/style/colors";
import { getLov } from "../../../utils/lov";

const styles = {
  container: {},
  arrowContainer: {
    width: "30px",
    height: "30px",
    borderRadius: "4px",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    "&:hover": {
      backgroundColor: colors.lightGreenBg,
    },
  },
  todayButton: {
    color: colors.primary,
    cursor: "pointer",
    textTransform: "uppercase",
    fontWeight: 500,
  },
};

const SchedulerFilters = (props) => {
  const {
    classes,
    companies,
    calendarView,
    setCalendarView,
    startDate,
    setStartDate,
    filters,
    handleFiltersChange,
    timeSlots,
    refresh,
    language,
    type,
  } = props;
  const [scheduleTypes, setScheduleTypes] = React.useState();

  React.useEffect(() => {
    fetchLov();
    // eslint-disable-next-line
  }, [type]);

  const fetchLov = async () => {
    try {
      let scheduleTypes = await getLov("scheduleTypes");
      scheduleTypes = filter(
        scheduleTypes,
        (t) =>
          t.parent_schedule_type_id === (type === HSP ? 1 : 2) &&
          t.is_schedulable
      );
      setScheduleTypes(scheduleTypes);
    } catch (e) {
      console.log(e);
    }
  };

  const changePeriod = (prev) => {
    let msAmount;
    if (calendarView === calendarViews.weekly) {
      msAmount = 604800000;
    } else if (calendarView === calendarViews.daily) {
      msAmount = 86400000;
    }
    if (prev) {
      setStartDate(new Date(startDate.getTime() - msAmount));
    } else {
      setStartDate(new Date(startDate.getTime() + msAmount));
    }
  };

  const handleDateSelect = (name, date) => {
    const day = new Date(date).getDay();
    if (calendarView === calendarViews.daily) {
      setStartDate(new Date(date));
    } else if (calendarView === calendarViews.weekly) {
      setStartDate(new Date(new Date(date).getTime() - day * 86400000));
    }
  };

  const formatDateLabel = (startDate) => {
    if (calendarView === calendarViews.weekly) {
      const start = new Date(startDate);
      const end = new Date(start.getTime() + 604800000);
      return `${formatDate(start, "DD/MM")} - ${formatDate(end, "DD/MM")}`;
    } else if (calendarView === calendarViews.daily) {
      const start = new Date(startDate);
      return `${formatDate(start, "DD/MM")}`;
    }
  };

  const siteZones = get(
    filter(
      get(
        filter(
          companies,
          (c) =>
            size(
              filter(get(c, ["sites"]), (s) => s.id === get(filters, ["site"]))
            ) > 0
        ),
        [0, "sites"]
      ),
      (s) => s.id === get(filters, ["site"])
    ),
    [0, "zones"]
  );

  return (
    <Grid
      item
      container
      data-test="scheduler-filters-component"
      className={classes.container}
    >
      <Box className="flexCenter" style={{ width: "42px" }}>
        <AddOccurrenceDialog
          timeSlots={timeSlots}
          initialDate={startDate}
          refresh={refresh}
          type={type}
        />
      </Box>
      <Box style={{ width: "170px", marginRight: "30px" }}>
        <FormSection
          formValue={[get(filters, ["site"]) || ""]}
          setFormValue={handleFiltersChange}
          title={"site"}
          id={"site"}
          type="select"
          options={{
            categories: true,
            options: map(companies, (c) => ({
              ...c,
              options: get(c, ["sites"]),
            })),
          }}
        />
      </Box>
      <Box style={{ width: "170px", marginRight: "30px" }}>
        <FormSection
          formValue={get(filters, ["zones"]) || []}
          setFormValue={handleFiltersChange}
          title2={I18n.get("companies_zones")}
          id={"zones"}
          type="select"
          options={{
            nullForAll: true,
            selectAll: true,
            multipleSelect: true,
            options: map(siteZones, (z) => ({
              id: z.id,
              name: z.name,
            })),
          }}
        />
      </Box>
      <Box style={{ width: "170px", marginRight: "30px" }}>
        <FormSection
          formValue={get(filters, ["scheduleType"]) || []}
          setFormValue={handleFiltersChange}
          title2={I18n.get("scheduler_scheduleType")}
          id={"scheduleType"}
          type="select"
          options={{
            nullForAll: true,
            selectAll: true,
            multipleSelect: true,
            options: map(scheduleTypes, (item) => ({
              id: get(item, ["id"]),
              name: get(item, ["name", language]),
            })),
          }}
        />
      </Box>
      <Box style={{ width: "125px", marginRight: "30px" }}>
        <FormSection
          formValue={[calendarView]}
          setFormValue={(id, index, value) => { setCalendarView(value); }}
          title2="‎‎ "
          id={"view"}
          type="select"
          options={{
            options: map(calendarViews, (view) => ({
              id: view,
              name: I18n.get(`common_${view}`),
            })),
          }}
        />
      </Box>
      <Box className="flexCenter">
        <span
          className={classes.arrowContainer}
          onClick={() => changePeriod(true)}
        >
          <img
            src={images.leftArrowLight}
            alt="left-arrow"
            data-test="scheduler-filters-previous-arrow"
          />
        </span>
        <span
          className={classes.arrowContainer}
          onClick={() => changePeriod(false)}
        >
          <img
            src={images.rightArrowLight}
            alt="right-arrow"
            data-test="scheduler-filters-next-arrow"
          />
        </span>
      </Box>
      <Grid container direction="column" style={{ width: "160px" }}>
        <Grid item style={{ textAlign: "right" }}>
          <span
            className={classes.todayButton}
            onClick={() => handleDateSelect(null, setToMidnight(Date.now()))}
          >
            {I18n.get("dashboard_today")}
          </span>
        </Grid>
        <DatePicker
          name="startDate"
          value={startDate}
          onChange={handleDateSelect}
          dateOnly
          formatLabel={formatDateLabel}
        />
      </Grid>
      <Box
        className="flexCenter"
        style={{ width: "250px", marginLeft: "30px" }}
      >
        <ProfileSearchInput
          profileType={1}
          title2={I18n.get("profiles_profile")}
          selectProfile={(profile) =>
            handleFiltersChange("profile", null, profile)
          }
          selectedProfile={get(filters, ["profile"])}
          noMinHeight
          noIcon
          noProfileDisplay
          disableNewProfile
          style={{ width: "100%" }}
        />
      </Box>
    </Grid>
  );
};

const mapStateToProps = (state) => {
  return {
    companies: get(state, ["user", "companies"]),
  };
};

export default connect(mapStateToProps)(withStyles(styles)(SchedulerFilters));
